"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, FileCheck, CircleCheck as CheckCircle2, FileSpreadsheet, Settings2, Cpu, Layers, ArrowRight, Award, FileText } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
const qualificationPhases = [{
  step: "01",
  phase: "DQ",
  name: "Design Qualification",
  description: "Detailed evaluation ensuring instrument specifications and technical architecture align with user requirements and analytical intent.",
  deliverables: ["User Requirements Specification (URS) review", "Vendor capability verification", "Design parameter traceability matrix"]
}, {
  step: "02",
  phase: "IQ",
  name: "Installation Qualification",
  description: "Verification that the system, auxiliary units, and software modules are delivered, positioned, and connected according to manufacturer specs.",
  deliverables: ["Physical asset & serial number logging", "Environmental and electrical check", "Firmware and software baseline configuration"]
}, {
  step: "03",
  phase: "OQ",
  name: "Operational Qualification",
  description: "Systematic testing verifying that all operational parameters, alarms, sensors, and functional ranges operate within defined acceptance limits.",
  deliverables: ["Temperature, pressure & optical calibration", "Diagnostic fault & alarm triggering tests", "Software security and access-control verification"]
}, {
  step: "04",
  phase: "PQ",
  name: "Performance Qualification",
  description: "Demonstration that the analytical system consistently produces reproducible, compliant results under routine operating and workload conditions.",
  deliverables: ["Replicate assay precision testing", "Sample recovery & matrix validation", "Standard operating procedure (SOP) dry-run"]
}];
const standardsList = [{
  id: "data-integrity",
  label: "Electronic Records & Data Integrity",
  title: "Data Integrity & Electronic Records",
  summary: "Complete compliance execution for software-controlled instrumentation, ensuring full data auditability and tamper-evident audit trails.",
  points: ["Secure time-stamped audit trails for every user action and data modification", "Multi-tiered role permissions with individual digital signature authorities", "Automated raw-data backups, archive protection, and disaster recovery workflows", "System verification testing against ALCOA+ data integrity principles"]
}, {
  id: "gamp-5",
  label: "GAMP 5 Principles",
  title: "Risk-Based Life Cycle Validation",
  summary: "Application of GAMP 5 principles is supported throughout system design, deployment and operational phases.",
  points: ["Structured categorization of software (Category 3/4/5) and custom routines", "Impact assessment and formal Failure Mode and Effects Analysis (FMEA)", "Periodic review schedules and change-control management protocols", "Traceability documentation mapping functional specs directly to test scripts"]
}, {
  id: "pharmacopoeia",
  label: "Regulatory Documentation Support",
  title: "Laboratory & Regulatory Standards Support",
  summary: "Comprehensive documentation support and alignment with standard laboratory guidelines, compendial methods, and qualification requirements.",
  points: ["Traceable reference materials used in verification steps, where applicable", "Instrument measurement uncertainty documentation for accredited test methods", "Re-qualification schedules tailored to manufacturer and regulatory intervals", "Detailed certificates of compliance supplied ready for regulatory audit inspection"]
}];
const complianceFaqs = [{
  question: "What documentation is provided after qualification completion?",
  answer: "Every qualification execution concludes with an audit-ready binder and digital package containing the signed protocol, raw execution records, calibration certificates with unbroken NIST/UKAS traceability, instrument serial checks, and an executive summary statement signed by certified validation engineers."
}, {
  question: "How do you support re-qualification and change control?",
  answer: "We offer scheduled annual or bi-annual re-qualification (RQ/OQ) services, as well as targeted requalification protocols when major hardware components are serviced, optical benches replaced, or software firmware versions upgraded."
}, {
  question: "Are qualification protocols pre-approved prior to onsite execution?",
  answer: "Yes. All validation protocols (IQ, OQ, PQ) are provided in draft format for your quality assurance (QA) and regulatory teams to review, customize, and pre-approve before our engineers commence onsite testing."
}, {
  question: "Do you support integrated laboratory network and LIMS validation?",
  answer: "Our specialists validate communication interfaces, network file pathways, and centralized data repositories to ensure automated data exports maintain complete structural integrity and security compliance."
}];
export default function Compliance() {
  const prefersReducedMotion = useReducedMotion();
  const containerAnimation = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.1,
        duration: 0.4
      }
    }
  };
  const itemAnimation = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0,
      y: prefersReducedMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="qualificationandcompliance" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container flex flex-col gap-12 md:gap-16">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="flex flex-col items-center text-center max-w-3xl mx-auto space-y-4">
          <Badge variant="outline" className="px-3 py-1 font-medium border-primary/30 text-primary">
            <ShieldCheck className="size-3.5 mr-1" />
            Regulatory Assurance & Lifecycle Validation
          </Badge>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Qualification Procedures & Compliance Standards
          </h2>

          <p className="text-base md:text-lg font-sans text-muted-foreground leading-relaxed">
            Standardized, audit-ready validation frameworks engineered for regulated pharmaceutical, biopharma, and testing laboratories across Ireland and the UK.
          </p>
        </motion.div>

        {/* 4-Phase Qualification Protocol */}
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-border pb-4">
            <div>
              <span className="text-xs uppercase tracking-wider font-semibold text-primary">
                Lifecycle Framework
              </span>
              <h3 className="text-xl md:text-2xl font-default font-semibold text-foreground">
                Four-Stage Qualification Model (4Q)
              </h3>
            </div>
            <p className="text-sm font-sans text-muted-foreground max-w-md">
              Rigorous testing protocols aligned with international GMP directives from initial specification to routine deployment.
            </p>
          </div>

          <motion.div variants={containerAnimation} initial="hidden" whileInView="show" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {qualificationPhases.map((phase, i) => <motion.div key={phase.phase} variants={itemAnimation} data-index={i} className="h-full">
                <Card className="h-full border border-border bg-card shadow-xs hover:border-primary/40 transition-[border-color,box-shadow]">
                  <CardHeader className="space-y-2 pb-3">
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-default font-bold text-primary">
                        {phase.phase}
                      </span>
                      <span className="text-xs font-mono font-medium px-2 py-0.5 rounded-sm bg-muted text-muted-foreground">
                        Phase {phase.step}
                      </span>
                    </div>
                    <CardTitle className="text-base font-default font-semibold text-card-foreground">
                      {phase.name}
                    </CardTitle>
                    <CardDescription className="text-xs leading-normal">
                      {phase.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2 border-t border-border/60">
                    <div className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5">
                      <FileCheck className="size-3.5 text-primary" />
                      Key Deliverables:
                    </div>
                    <ul className="space-y-1.5 text-xs text-muted-foreground">
                      {phase.deliverables.map((item, dIdx) => <li key={dIdx} className="flex items-start gap-1.5">
                          <CheckCircle2 className="size-3 text-primary shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>)}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>)}
          </motion.div>
        </div>

        {/* Regulatory Standards & Compliance Frameworks */}
        <div className="rounded-xl border border-border bg-secondary/40 p-6 md:p-8">
          <div className="max-w-2xl mb-6">
            <span className="text-xs uppercase tracking-wider font-semibold text-primary">
              Standards & Integrity
            </span>
            <h3 className="text-2xl font-default font-bold text-foreground mt-1">
              Regulatory Alignment & Data Integrity
            </h3>
            <p className="text-sm text-muted-foreground mt-1.5 font-sans">
              Qualification support is structured with reference to HPRA, MHRA, FDA, and EMA guidance.
            </p>
          </div>

          <Tabs defaultValue="data-integrity" className="w-full">
            <TabsList className="grid grid-cols-1 sm:grid-cols-3 w-full max-w-2xl mb-6 bg-muted">
              {standardsList.map((std, i) => <TabsTrigger key={std.id} value={std.id} data-index={i} className="text-xs md:text-sm font-medium">
                  {std.label}
                </TabsTrigger>)}
            </TabsList>

            {standardsList.map((std, i) => <TabsContent key={std.id} value={std.id} data-index={i} className="focus-visible:outline-none">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start bg-card p-6 md:p-8 rounded-lg border border-border shadow-xs">
                  <div className="lg:col-span-5 space-y-3">
                    <div className="inline-flex p-2.5 rounded-md bg-primary/10 text-primary">
                      {std.id === "data-integrity" && <Cpu className="size-6" />}
                      {std.id === "gamp-5" && <Layers className="size-6" />}
                      {std.id === "pharmacopoeia" && <Award className="size-6" />}
                    </div>
                    <h4 className="text-xl font-default font-bold text-card-foreground">
                      {std.title}
                    </h4>
                    <p className="text-sm font-sans text-muted-foreground leading-relaxed">
                      {std.summary}
                    </p>
                    <div className="pt-2">
                      <Button variant="outline" size="sm" asChild>
                        <Link to="/ServicesPage" className="text-xs">
                          <FileText className="size-3.5 mr-1" />
                          View Full Documentation Scope
                        </Link>
                      </Button>
                    </div>
                  </div>

                  <div className="lg:col-span-7 bg-background p-5 rounded-md border border-border">
                    <h5 className="text-xs uppercase tracking-wider font-semibold text-foreground mb-3 flex items-center gap-1.5">
                      <Settings2 className="size-3.5 text-primary" />
                      Validation Scope & Protocol Specifications
                    </h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {std.points.map((point, pIdx) => <div key={pIdx} className="flex items-start gap-2 text-xs text-muted-foreground p-2.5 rounded-sm bg-muted/40 border border-border/50">
                          <CheckCircle2 className="size-3.5 text-primary shrink-0 mt-0.5" />
                          <span>{point}</span>
                        </div>)}
                    </div>
                  </div>
                </div>
              </TabsContent>)}
          </Tabs>
        </div>

        {/* Deliverables & FAQs Split Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left: Validation Deliverables Checklist */}
          <div className="lg:col-span-5 space-y-4">
            <div className="border-b border-border pb-3">
              <span className="text-xs uppercase tracking-wider font-semibold text-primary">
                Audit Assurance
              </span>
              <h3 className="text-xl font-default font-bold text-foreground mt-1">
                Standard Qualification Deliverables
              </h3>
            </div>
            <p className="text-sm text-muted-foreground font-sans">
              Each validation engagement provides a complete, structured documentation package tailored for immediate submission during GMP inspections.
            </p>

            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-3 p-3.5 rounded-lg border border-border bg-card">
                <FileSpreadsheet className="size-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-card-foreground">NIST & UKAS Traceable Certificates</h4>
                  <p className="text-xs text-muted-foreground">Certified calibration standards with unbroken traceability chain documentation.</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3.5 rounded-lg border border-border bg-card">
                <FileCheck className="size-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-card-foreground">Pre-Approved Test Execution Scripts</h4>
                  <p className="text-xs text-muted-foreground">Detailed test step scripts with clear acceptance criteria and deviation logging.</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3.5 rounded-lg border border-border bg-card">
                <Award className="size-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-card-foreground">Formal Certificate of Qualification</h4>
                  <p className="text-xs text-muted-foreground">Final validation sign-off statement for laboratory instrument history files.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Accordion FAQs */}
          <div className="lg:col-span-7 space-y-4">
            <div className="border-b border-border pb-3">
              <span className="text-xs uppercase tracking-wider font-semibold text-primary">
                Frequently Asked Questions
              </span>
              <h3 className="text-xl font-default font-bold text-foreground mt-1">
                Compliance & Protocol Details
              </h3>
            </div>

            <Accordion type="single" collapsible className="w-full space-y-2">
              {complianceFaqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border border-border rounded-lg bg-card px-4">
                  <AccordionTrigger className="text-left font-default font-medium text-sm text-card-foreground hover:no-underline py-3.5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-xs font-sans text-muted-foreground leading-relaxed pb-3.5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>

        {/* CTA Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 p-6 md:p-8 rounded-xl bg-card border border-border shadow-xs">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="text-lg md:text-xl font-default font-bold text-card-foreground">
              Need custom qualification protocols or on-site validation?
            </h3>
            <p className="text-xs md:text-sm text-muted-foreground font-sans">
              Speak with our validation engineering team to schedule IQ/OQ/PQ execution across Ireland and the UK.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Button variant="outline" asChild>
              <Link to="/ServicesPage">
                View All Services
              </Link>
            </Button>
            <Button asChild>
              <Link to="/ContactPage">
                <span>Request Validation Protocol</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}